import { ReadonlyVec3, vec3 } from 'gl-matrix';
import { LayerBit } from '~/engine/view/layers/LayerBit';
import {
    Color,
    DicomContour,
    Roi,
    RoiGenerationAlgorithmType,
    RoiListener,
} from '../../../domain/dicom/dicomstructureset';
import { ViewManager } from '../../view/ViewManager';
import { VolumeViewUpdateEvent } from '../../view/events/VolumeViewUpdateEvent';
import { Grid } from '../common/Grid';
import { RotatedBoundingBox } from '../common/RotatedBoundingBox';
import { Contour } from './Contour';
import { ContourSequence } from './ContourSequence';
import { ContourSlice } from './ContourSlice';
import { ContourTexture } from './ContourTexture';
import {
    NotifyPropertyChanged,
    NotifyPropertyChangedHelper,
} from '../notifyPropertyChanged';
import { UndoStack } from './UndoStack';

/**
 * Helper class to manage the region of interest texture modifications.
 */
export class TextureWrite {
    constructor(
        public readonly roi: RegionOfInterest,
        public readonly operation: string
    ) {}

    public get texture(): ContourTexture | undefined {
        return this.roi.texture;
    }

    public finish() {
        this.roi.onTextureChanged(this.operation);
    }
}

export class RegionOfInterest
    implements RoiListener, NotifyPropertyChanged<RegionOfInterest>
{
    public readonly roiNumber: number;

    private _color: vec3;

    private _contourThickness = 1;

    private readonly contourSequence: ContourSequence;

    private _allSlicesChanged = false;
    private _modificationStarted = false;

    private contourTexture?: ContourTexture;

    private readonly _undoStack: UndoStack = new UndoStack();

    public constructor(
        private readonly _parentRoi: Roi,
        public readonly _imageGrid: Grid
    ) {
        _parentRoi.addListener(this);
        this.roiNumber = _parentRoi.roiNumber;
        this._color = vec3.fromValues(
            _parentRoi.color.R / 255,
            _parentRoi.color.G / 255,
            _parentRoi.color.B / 255
        );
        const slices = RegionOfInterest.toSlices(
            _parentRoi.contours,
            _imageGrid
        );
        this.contourSequence = new ContourSequence(_imageGrid, slices);
    }

    public get dicomParent(): Roi {
        return this._parentRoi;
    }

    private readonly _notifyPropertyChangedHelper =
        new NotifyPropertyChangedHelper<RegionOfInterest>();

    public addListener(listener: (arg0: RegionOfInterest) => void): void {
        this._notifyPropertyChangedHelper.addListener(listener);
    }

    public removeListener(listener: (arg0: RegionOfInterest) => void): void {
        this._notifyPropertyChangedHelper.removeListener(listener);
    }

    private notifyPropertyChangedListeners(): void {
        this._notifyPropertyChangedHelper.notifyListeners(this);
    }

    public get isVisible(): boolean {
        return this._parentRoi.visible;
    }

    public set visibility(visible: boolean) {
        if (this._parentRoi.visible !== visible) {
            this._parentRoi.visible = visible;
        }
    }

    public setVisibilityNoNotification(visible: boolean): void {
        this._parentRoi.setVisibilityNoNotification(visible);
    }

    public get contourThickness(): number {
        return this._contourThickness;
    }

    public set contourThickness(contourThickness: number) {
        this._contourThickness = contourThickness;
    }

    /**
     * Method to get reference to {@link contourSequence}-data.\
     * This updates the {@link ContourSequence} before returning it, if the data is not up-to-date.
     */
    public get sequence(): ContourSequence {
        if (!this.isSequenceUpToDate) {
            if (this.contourTexture) {
                this.updateSequence();
            } else {
                throw new Error(
                    'RegionOfInterest get sequence(): texture is undefined'
                );
            }
        }
        return this.contourSequence;
    }

    public get hasBeenModified(): boolean {
        return this._allSlicesChanged;
    }

    public get isSequenceUpToDate(): boolean {
        return this._allSlicesChanged === false;
    }

    private checkModificationFlag() {
        if (this._modificationStarted) {
            console.warn(
                `${this.name} updateSequence(): modifications flag on`
            );
        }
    }

    private updateSequence(): void {
        this.checkModificationFlag();
        this.contourSequence.updateFromContourTexture(this.contourTexture!);
        this._allSlicesChanged = false;
    }

    private _renderErrors?: string = '';
    /**
     * Method to get reference to {@link contourTexture}-data.\
     * This updates the {@link ContourTexture} before returning it, if the data is not up-to-date.
     */
    public get texture(): ContourTexture | undefined {
        if (
            !this.contourTexture &&
            this.contourSequence.contourSlices.length > 0
        ) {
            this.contourTexture = new ContourTexture(this._imageGrid);
            this._renderErrors = this.contourTexture.update(
                this.contourSequence
            );
        }
        return this.contourTexture;
    }

    public get renderErrors(): string | undefined {
        return this._renderErrors;
    }

    //TODO: Critical: Temporary for debugging! Remove this method ASAP
    public set texture(texture: ContourTexture | undefined) {
        this.contourTexture = texture;
    }

    public get color(): ReadonlyVec3 {
        return this._color;
    }

    public get volumeCm3(): number {
        if (!this.contourTexture) {
            return 0.0;
        }
        return this.contourTexture.volumeCm3;
    }

    /**
     * Method to get the BoundingBox of the structure texture.
     */
    public get boundingBox(): RotatedBoundingBox {
        return this.texture?.fullBoundingBox ?? new RotatedBoundingBox();
    }

    /**
     * Method to inform this {@link RegionOfInterest} that the {@link ContourTexture} got some updates making it the most up-to-date datamodel.\
     * This sets the {@link dataModelStates} to 0b10, which means that only {@link ContourTexture} data is up-to-date and other datamodels are outdated.\
     * Other datamodels will be updated whenever they are accessed.
     */
    public onTextureChanged(operation: string): void {
        this._modificationStarted = false;
        this._allSlicesChanged = true;
        if (!this.contourTexture) {
            throw new Error(
                `RegionOfInterest onTextureChanged: texture is undefined`
            );
        }
        this.contourTexture.invalidateCachedPixels();
        this._undoStack.push(
            this.contourTexture,
            this.contourTexture.pixels,
            operation
        );
        this.notifyPropertyChangedListeners();
        if (
            this._parentRoi.roiGenerationAlgorithm ===
            RoiGenerationAlgorithmType.AUTOMATIC
        ) {
            this._parentRoi.roiGenerationAlgorithm =
                RoiGenerationAlgorithmType.SEMI_AUTOMATIC;
        }
    }

    public undoContourChange() {
        this._undoStack.undo(this.contourTexture!);
        this.notifyPropertyChangedListeners();
    }

    public redoContourChange() {
        this._undoStack.redo(this.contourTexture!);
        this.notifyPropertyChangedListeners();
    }

    public canUndo(): boolean {
        return this._undoStack.canUndo();
    }

    public canRedo(): boolean {
        return this._undoStack.canRedo();
    }

    /**
     * Method to generate {@link SliceData slices} for the {@link contourTexture}.\
     * {@link SliceData Slices} are retrieved from the {@link sliceSource}-parameter.\
     * This method is used when we need to have a {@link ContourTexture} with empty slices.
     * @param boundingBox {@link RotatedBoundingBox} that defines the 3D volume of the {@link RegionOfInterest}.
     * @param sliceSource {@link SliceSource} defining the slices that the {@link contourTexture} should align with.\
     * The slices are copied from this {@link SliceSource}.
     * @returns The {@link contourTexture} with the required slices.
     */
    public generateTextureSlices(
        boundingBox: RotatedBoundingBox
    ): ContourTexture {
        if (!this.contourTexture) {
            this.contourTexture = new ContourTexture(
                this._imageGrid,
                boundingBox
            );
        }
        // TODO: fix this method if it's still needed
        return this.contourTexture;
    }

    public static toSlices(
        dicomContours: DicomContour[],
        imageGrid: Grid
    ): ContourSlice[] {
        const normalDirection = imageGrid.getUnitZ();
        const contourSlices: ContourSlice[] = [];
        for (const dicomContour of dicomContours) {
            if (dicomContour.data.length > 2) {
                // minimum 3 points to create a contour
                const contour = new Contour(dicomContour.data.map(vec3.clone));
                const contourNormalPosition = vec3.dot(
                    contour.vertices[0],
                    normalDirection
                );
                // there may be multiple contours on the same slice
                const existingSlice = contourSlices.find((slice) =>
                    ContourSlice.isSameSlice(
                        slice.normalPosition,
                        contourNormalPosition
                    )
                );
                if (!existingSlice) {
                    contourSlices.push(
                        new ContourSlice(contourNormalPosition, [contour])
                    );
                } else {
                    existingSlice.addContour(contour);
                }
            }
        }

        return contourSlices;
    }

    public startModify(operation: string): TextureWrite {
        this._modificationStarted = true;
        if (this.texture && this._undoStack.size === 0) {
            const originalPixels = this.texture.pixels;
            if (originalPixels) {
                this._undoStack.push(this.texture, originalPixels, operation);
            }
        }

        return new TextureWrite(this, operation);
    }

    /**
     * Method to get the slice index in the Z-direction of the {@link contourTexture} that corresponds to the given plane coordinates.\
     * Method checks if a texture and its signed distance field exist.
     * Then it converts a set of 3D coordinates to image space and calculates a z-index based on the texture's size.
     * @returns The slice index of the {@link contourTexture} that corresponds to the given plane coordinates.
     */
    public getContourSliceZByPlaneCoords(planeCoordinates: number): number {
        if (!this.texture || this.texture.signedDistanceField === undefined) {
            return -1;
        }
        const imageCoords = this.texture.toImageSpace(
            vec3.fromValues(0, 0, planeCoordinates)
        );
        const textureHalfSize = this.texture.signedDistanceField.size[2] / 2;
        const sliceZindex = imageCoords[2] * textureHalfSize + textureHalfSize;
        return sliceZindex;
    }

    public get name(): string {
        return this._parentRoi.name;
    }

    /**
     * @override
     *
     * @param roi
     * @param color
     */
    onRoiColorChange(_roi: never, color: Color): void {
        vec3.set(this._color, color.R / 255, color.G / 255, color.B / 255);
        ViewManager.viewUpdateEvent(
            new VolumeViewUpdateEvent(
                this.boundingBox,
                LayerBit.CONTOUR_LAYER_BIT,
                true
            )
        );
    }

    onRoiVisibilityChange(_roi: never, _visible: boolean): void {
        ViewManager.viewUpdateEvent(
            new VolumeViewUpdateEvent(
                this.boundingBox,
                LayerBit.CONTOUR_LAYER_BIT,
                true
            )
        );
    }

    onRoiContourThicknessChange(_roi: never, contourThickness: number): void {
        this._contourThickness = contourThickness;
        ViewManager.viewUpdateEvent(
            new VolumeViewUpdateEvent(
                this.boundingBox,
                LayerBit.CONTOUR_LAYER_BIT,
                true
            )
        );
    }

    /**
     * Method to delete the {@link RegionOfInterest}.\
     * This de-allocates all the WebGL memory reserved by the different data models within this {@link RegionOfInterest}.
     */
    public delete() {
        this.texture?.delete();
        this._notifyPropertyChangedHelper.clearListeners();
    }
}
